import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIsAnkrAdmin, useIsHealthcareUser, useIsSalesUser, useIsPatientUser, useIsLoggedIn, useAuthContext } from '../context/AuthContext';
import { publicApi } from '../services/api';

/**
 * Navigation component for sidebar menu
 * @returns {JSX.Element} The sidebar navigation component
 */
const Navigation = () => {
  const location = useLocation();
  const isAnkrAdmin = useIsAnkrAdmin();
  const isHealthcareUser = useIsHealthcareUser();
  const isSalesUser = useIsSalesUser();
  const isPatientUser = useIsPatientUser();
  const isLoggedIn = useIsLoggedIn();
  const { logout } = useAuthContext();
  const [version, setVersion] = useState('');
  const [openSubmenu, setOpenSubmenu] = useState(null);

  useEffect(() => {
    const fetchVersion = async () => {
      const versionString = await publicApi.getVersion();
      setVersion(versionString);
    };
    fetchVersion();
  }, []);

  // Auto-open submenu if current path matches
  useEffect(() => {
    // Check if current path belongs to a specific submenu
    const miraRoutes = [
      '/admin/vcx-events',
      '/admin/voiceai-agents',
      '/admin/voiceai/credentials',
      '/admin/sales/analytics',
      '/admin/sales/call-logs',
      '/roi-calculator'
    ];

    const salesRoutes = [
      '/admin/sales/leads',
      '/admin/sales/opportunities',
      '/admin/sales/job-listings',
      '/admin/sales/organizations',
      '/admin/sales/users',
      '/admin/sales/roles',
      '/admin/sales/teams',
      '/admin/sales/call-logs',
      '/admin/sales/analytics',
      '/roi-calculator',
      '/admin/voiceai/credentials'
    ];

    const patientRoutes = ['/check-in', '/support-desk'];
    const usersRoutes = ['/admin/users', '/admin/providers', '/admin/roles'];
    const onboardingRoutes = ['/onboarding'];

    // Only auto-expand if user is on a route within that submenu
    if (miraRoutes.some(route => location.pathname.startsWith(route))) {
      setOpenSubmenu('mira');
    } else if (salesRoutes.some(route => location.pathname.startsWith(route))) {
      setOpenSubmenu('sales');
    } else if (patientRoutes.some(route => location.pathname.startsWith(route))) {
      setOpenSubmenu('patient');
    } else if (usersRoutes.some(route => location.pathname.startsWith(route))) {
      setOpenSubmenu('users');
    } else if (onboardingRoutes.some(route => location.pathname.startsWith(route))) {
      setOpenSubmenu('onboarding');
    } else {
      // Don't force any submenu open
      setOpenSubmenu(null);
    }
  }, [location.pathname]);
  const toggleSubmenu = (submenuId) => {
    setOpenSubmenu(openSubmenu === submenuId ? null : submenuId);
  };

  const handleRefreshVersion = async () => {
    try {
      const versionString = await publicApi.refreshVersion();
      setVersion(versionString);
    } catch (error) {
      setVersion('Error refreshing version');
    }
  };
  
  return (
    <>
      {/* Hidden element to prevent app.js errors */}
      <img id="header-lang-img" src="/dist/assets/images/flags/us.jpg" alt="Language" style={{ display: 'none' }} />
      
      {/* Sidemenu */}
      <div id="sidebar-menu">
        {/* Left Menu Start */}
        <ul className="metismenu list-unstyled" id="side-menu">
          {/* <li className="menu-title" data-key="t-menu">Patient Check-In</li> */}

        {/* Public navigation items - shown to logged-out users OR patient users */}
        {(!isLoggedIn || isPatientUser) && (
          <>
          <li className={location.pathname === '/check-in' ? 'mm-active' : ''}>
            <Link to="/check-in" className={location.pathname === '/check-in' ? 'active' : ''}>
              <i className="far fa-edit"></i>
              <span data-key="t-standard-checkin">Standard Check-In</span>
            </Link>
          </li>
          <li className={location.pathname === '/support-desk' ? 'mm-active' : ''}>
            <Link to="/support-desk" className={location.pathname === '/support-desk' ? 'active' : ''}>
              <i className="far fa-life-ring"></i>
              <span data-key="t-voice-demo">Support Desk</span>
            </Link>
          </li>
            <>
              <li className={`bottom-left-menu-item mb-4 ${location.pathname === '/admin/login' ? 'mm-active' : ''}`}>
                <Link to="/admin/login" className={location.pathname === '/admin/login' ? 'active' : ''}>
                  <i className="fas fa-sign-in-alt"></i>
                  <span data-key="t-admin-login">Admin</span>
                </Link>
              </li>
            </>
            </>
          )}
          
          {/* Sales user: only show sales module items */}
          {isSalesUser && (
            <>
              <li className={location.pathname === '/admin' ? 'mm-active' : ''}>
                <Link to="/admin" className={location.pathname === '/admin' ? 'active' : ''}>
                  <i className="fas fa-tachometer-alt me-2"></i>
                  <span data-key="t-admin-dashboard">Dashboard</span>
                </Link>
              </li>

              <li className={openSubmenu === 'sales' ? 'mm-active' : ''}>
                <a href="javascript: void(0);" className="has-arrow" onClick={() => toggleSubmenu('sales')} aria-expanded={openSubmenu === 'sales'}>
                  <i className="fas fa-chart-line me-2"></i>
                  <span data-key="t-sales-module">Sales Module</span>
                </a>
                <ul
                  className={`sub-menu mm-collapse ${openSubmenu === 'sales' ? 'mm-show' : ''}`}
                  aria-expanded={openSubmenu === 'sales'}
                  style={{ display: openSubmenu === 'sales' ? 'block' : 'none' }}
                >
                  <li className={location.pathname.startsWith('/admin/sales/leads') ? 'mm-active' : ''}>
                    <Link to="/admin/sales/leads" className={location.pathname.startsWith('/admin/sales/leads') ? 'active' : ''}>
                      <i className="fas fa-user-tie me-2"></i>
                      <span data-key="t-sales-leads">Sales Leads</span>
                    </Link>
                  </li>
                  <li className={location.pathname.startsWith('/admin/sales/opportunities') ? 'mm-active' : ''}>
                    <Link to="/admin/sales/opportunities" className={location.pathname.startsWith('/admin/sales/opportunities') ? 'active' : ''}>
                      <i className="fas fa-handshake me-2"></i>
                      <span data-key="t-sales-opportunities">Sales Opportunities</span>
                    </Link>
                  </li>
                  <li className={location.pathname.startsWith('/admin/sales/job-listings') ? 'mm-active' : ''}>
                    <Link to="/admin/sales/job-listings" className={location.pathname.startsWith('/admin/sales/job-listings') ? 'active' : ''}>
                      <i className="fas fa-briefcase me-2"></i>
                      <span data-key="t-job-listings">Job Listings</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/roi-calculator' ? 'mm-active' : ''}>
                    <Link to="/roi-calculator" className={location.pathname === '/roi-calculator' ? 'active' : ''}>
                      <i className="fas fa-calculator me-2"></i>
                      <span data-key="t-roi-calculator">ROI Calculator</span>
                    </Link>
                  </li>
                  {/* Analytics Dashboard - Disabled for sales users for now */}
                  {/* <li className={location.pathname === '/admin/sales/analytics' ? 'mm-active' : ''}>
                    <Link to="/admin/sales/analytics" className={location.pathname === '/admin/sales/analytics' ? 'active' : ''}>
                      <i className="fas fa-chart-bar me-2"></i>
                      <span data-key="t-analytics-dashboard">Analytics Dashboard</span>
                    </Link>
                  </li> */}
                  <li className={location.pathname === '/admin/sales/call-logs' ? 'mm-active' : ''}>
                    <Link to="/admin/sales/call-logs" className={location.pathname === '/admin/sales/call-logs' ? 'active' : ''}>
                      <i className="fas fa-person-chalkboard me-2"></i>
                      <span data-key="t-call-logs">Voice AI Coach</span>
                    </Link>
                  </li>
                  <li className="mt-2" data-key="t-admin"><Link className="text-primary" disabled>Admin Tools</Link></li>

                  <li className={location.pathname.startsWith('/admin/sales/organizations') ? 'mm-active' : ''}>
                    <Link to="/admin/sales/organizations" className={location.pathname.startsWith('/admin/sales/organizations') ? 'active' : ''}>
                      <i className="fas fa-building me-2"></i>
                      <span data-key="t-organizations">Organizations</span>
                    </Link>
                  </li>
                  <li className={location.pathname.startsWith('/admin/sales/users') ? 'mm-active' : ''}>
                    <Link to="/admin/sales/users" className={location.pathname.startsWith('/admin/sales/users') ? 'active' : ''}>
                      <i className="fas fa-user-tag me-2"></i>
                      <span data-key="t-sales-users">Sales Users</span>
                    </Link>
                  </li>
                  <li className={location.pathname.startsWith('/admin/sales/roles') ? 'mm-active' : ''}>
                    <Link to="/admin/sales/roles" className={location.pathname.startsWith('/admin/sales/roles') ? 'active' : ''}>
                      <i className="fas fa-id-badge me-2"></i>
                      <span data-key="t-sales-roles">Sales Roles</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/voiceai/credentials' ? 'mm-active' : ''}>
                    <Link to="/admin/voiceai/credentials" className={location.pathname === '/admin/voiceai/credentials' ? 'active' : ''}>
                      <i className="fas fa-key me-2"></i>
                      <span data-key="t-credentials-manager">Credentials Manager</span>
                    </Link>
                  </li>
                </ul>
              </li>
              
                {/* Link for Logged In User's Profile */}
                <li className={location.pathname.startsWith('/admin/profile') ? 'mm-active' : ''}>
                  <Link to="/admin/profile" className={location.pathname.startsWith('/admin/profile') ? 'active' : ''}>
                    <i className="fas fa-user-circle me-2"></i>
                    <span data-key="t-note-templates">My Profile</span>
                  </Link>
                </li>
                  

              <li>
                <a href="#" onClick={(e) => { e.preventDefault(); logout('/admin/login'); }}>
                  <i className="fas fa-sign-out-alt me-2"></i>
                  <span data-key="t-logout">Logout</span>
                </a>
              </li>
            </>
          )}

          {/* Only show Admin Tools section if user is logged in */}
          {(isAnkrAdmin || isHealthcareUser) && (
            <>
              <li className="menu-title" data-key="t-admin">Admin Tools</li>
              
              <li className={location.pathname === '/admin' ? 'mm-active' : ''}>
                <Link to="/admin" className={location.pathname === '/admin' ? 'active' : ''}>
                  <i className="fas fa-tachometer-alt me-2"></i>
                  <span data-key="t-admin-dashboard">Dashboard</span>
                </Link>
              </li>
              
              <li className={location.pathname.startsWith('/admin/cases') ? 'mm-active' : ''}>
                <Link to="/admin/cases" className={location.pathname.startsWith('/admin/cases') ? 'active' : ''}>
                  <i className="fas fa-folder-open me-2"></i>
                  <span data-key="t-cases">Cases</span>
                </Link>
              </li>
              
              <li className={location.pathname.startsWith('/admin/health-systems') ? 'mm-active' : ''}>
                <Link to="/admin/health-systems" className={location.pathname.startsWith('/admin/health-systems') ? 'active' : ''}>
                  <i className="fas fa-house-medical me-2"></i>
                  <span data-key="t-health-systems">Health Systems</span>
                </Link>
              </li>

              <li className={openSubmenu === 'mira' ? 'mm-active' : ''}>
                <a href="javascript: void(0);" className="has-arrow" onClick={() => toggleSubmenu('mira')} aria-expanded={openSubmenu === 'mira'} >
                  <i className="fas fa-microphone-alt me-2"></i>
                  <span data-key="t-mira-config">Mira & Frank</span>
                </a>
                <ul
                  className={`sub-menu mm-collapse ${openSubmenu === 'mira' ? 'mm-show' : ''}`}
                  aria-expanded={openSubmenu === 'mira'}
                  style={{ display: openSubmenu === 'mira' ? 'block' : 'none' }}
                >
                  <li className={location.pathname === '/admin/vcx-events/domain/appointments' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/appointments" className={location.pathname === '/admin/vcx-events/domain/appointments' ? 'active' : ''}>
                      <i className="fas fa-calendar-check me-2"></i>
                      <span data-key="t-vcx-events-appointments">Appointments</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/medications' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/medications" className={location.pathname === '/admin/vcx-events/domain/medications' ? 'active' : ''}>
                      <i className="fas fa-pills me-2"></i>
                      <span data-key="t-vcx-events-medications">Medications</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/medicalIssues' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/medicalIssues" className={location.pathname === '/admin/vcx-events/domain/medicalIssues' ? 'active' : ''}>
                      <i className="fas fa-stethoscope me-2"></i>
                      <span data-key="t-vcx-events-medical">Medical Issues</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/billingInsurance' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/billingInsurance" className={location.pathname === '/admin/vcx-events/domain/billingInsurance' ? 'active' : ''}>
                      <i className="fas fa-credit-card me-2"></i>
                      <span data-key="t-vcx-events-billing">Billing/Insurance</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/medicalRecords' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/medicalRecords" className={location.pathname === '/admin/vcx-events/domain/medicalRecords' ? 'active' : ''}>
                      <i className="fas fa-file-medical me-2"></i>
                      <span data-key="t-vcx-events-records">Medical Records</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/new_patient' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/new_patient" className={location.pathname === '/admin/vcx-events/domain/new_patient' ? 'active' : ''}>
                      <i className="fas fa-user-plus me-2"></i>
                      <span data-key="t-vcx-events-new-patient">New Patients</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/providerCalls' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/providerCalls" className={location.pathname === '/admin/vcx-events/domain/providerCalls' ? 'active' : ''}>
                      <i className="fas fa-user-md me-2"></i>
                      <span data-key="t-vcx-events-provider">Provider Calls</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/generalInquiries' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/generalInquiries" className={location.pathname === '/admin/vcx-events/domain/generalInquiries' ? 'active' : ''}>
                      <i className="fas fa-question-circle me-2"></i>
                      <span data-key="t-vcx-events-general">General Inquiries</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/vendorCalls' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/vendorCalls" className={location.pathname === '/admin/vcx-events/domain/vendorCalls' ? 'active' : ''}>
                      <i className="fas fa-phone-volume me-2"></i>
                      <span data-key="t-vcx-events-vendor">Vendor Calls</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events/domain/unknown' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events/domain/unknown" className={location.pathname === '/admin/vcx-events/domain/unknown' ? 'active' : ''}>
                      <i className="fas fa-question me-2"></i>
                      <span data-key="t-vcx-events-unknown">Unknown/Other</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/vcx-events' ? 'mm-active' : ''}>
                    <Link to="/admin/vcx-events" className={location.pathname === '/admin/vcx-events' ? 'active' : ''}>
                      <i className="fas fa-phone me-2"></i>
                      <span data-key="t-vcx-events">All Events</span>
                    </Link>
                  </li>

                  <li className="mt-2" data-key="t-admin"><Link className="text-primary" disabled>Admin Tools</Link></li>

                  <li className={location.pathname.includes('/voiceai-agents') ? 'mm-active' : ''}>
                    <Link to="/admin/voiceai-agents" className={location.pathname.includes('/voiceai-agents') ? 'active' : ''}>
                      <i className="fas fa-robot me-2"></i>
                      <span data-key="t-voiceai-agents">Voice AI Agents</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/roi-calculator' ? 'mm-active' : ''}>
                    <Link to="/roi-calculator" className={location.pathname === '/roi-calculator' ? 'active' : ''}>
                      <i className="fas fa-calculator me-2"></i>
                      <span data-key="t-roi-calculator">ROI Calculator</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/sales/analytics' ? 'mm-active' : ''}>
                    <Link to="/admin/sales/analytics" className={location.pathname === '/admin/sales/analytics' ? 'active' : ''}>
                      <i className="fas fa-chart-bar me-2"></i>
                      <span data-key="t-analytics-dashboard">Analytics Dashboard</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/sales/call-logs' ? 'mm-active' : ''}>
                    <Link to="/admin/sales/call-logs" className={location.pathname === '/admin/sales/call-logs' ? 'active' : ''}>
                      <i className="fas fa-person-chalkboard me-2"></i>
                      <span data-key="t-call-logs">Voice AI Coach</span>
                    </Link>
                  </li>
                  <li className={location.pathname === '/admin/voiceai/credentials' ? 'mm-active' : ''}>
                    <Link to="/admin/voiceai/credentials" className={location.pathname === '/admin/voiceai/credentials' ? 'active' : ''}>
                      <i className="fas fa-key me-2"></i>
                      <span data-key="t-credentials-manager">Credentials Manager</span>
                    </Link>
                  </li>
                </ul>

                <li className={location.pathname === '/admin/referrals' || (location.pathname.startsWith('/admin/referrals') && !location.pathname.includes('/settings')) ? 'mm-active' : ''}>
                  <Link to="/admin/referrals" className={location.pathname === '/admin/referrals' || (location.pathname.startsWith('/admin/referrals') && !location.pathname.includes('/settings')) ? 'active' : ''}>
                    <i className="fas fa-clipboard-list me-2"></i>
                    <span data-key="t-referral-management">Referral Management</span>
                  </Link>
                </li>
              </li>

              {isAnkrAdmin && (
                <li className={openSubmenu === 'onboarding' ? 'mm-active' : ''}>
                  <a href="javascript: void(0);" className="has-arrow" onClick={() => toggleSubmenu('onboarding')} aria-expanded={openSubmenu === 'onboarding'} >
                    <i className="fas fa-rocket me-2"></i>
                    <span data-key="t-onboarding-module">Onboarding Module</span>
                  </a>
                  <ul
                    className={`sub-menu mm-collapse ${openSubmenu === 'onboarding' ? 'mm-show' : ''}`}
                    aria-expanded={openSubmenu === 'onboarding'}
                    style={{ display: openSubmenu === 'onboarding' ? 'block' : 'none' }}
                  >
                    <li className={location.pathname === '/onboarding/dashboard' ? 'mm-active' : ''}>
                      <Link to="/onboarding/dashboard" className={location.pathname === '/onboarding/dashboard' ? 'active' : ''}>
                        <i className="fas fa-tachometer-alt me-2"></i>
                        <span data-key="t-onboarding-dashboard">Dashboard</span>
                      </Link>
                    </li>
                    <li className={location.pathname === '/onboarding/ehr-connect' ? 'mm-active' : ''}>
                      <Link to="/onboarding/ehr-connect" className={location.pathname === '/onboarding/ehr-connect' ? 'active' : ''}>
                        <i className="fas fa-link me-2"></i>
                        <span data-key="t-ehr-connect">EHR Connect</span>
                      </Link>
                    </li>
                    <li className={location.pathname === '/onboarding/settings' ? 'mm-active' : ''}>
                      <Link to="/onboarding/settings" className={location.pathname === '/onboarding/settings' ? 'active' : ''}>
                        <i className="fas fa-cog me-2"></i>
                        <span data-key="t-onboarding-settings">Settings</span>
                      </Link>
                    </li>
                    <li className={location.pathname === '/onboarding/help' ? 'mm-active' : ''}>
                      <Link to="/onboarding/help" className={location.pathname === '/onboarding/help' ? 'active' : ''}>
                        <i className="fas fa-question-circle me-2"></i>
                        <span data-key="t-onboarding-help">Help</span>
                      </Link>
                    </li>
                  </ul>
                </li>
                )}
              {/* Sales user: only show sales module items */}
              {(isAnkrAdmin || isSalesUser) && (
                <>
                  <li className={openSubmenu === 'sales' ? 'mm-active' : ''}>
                    <a href="javascript: void(0);" className="has-arrow" onClick={() => toggleSubmenu('sales')} aria-expanded={openSubmenu === 'sales'} >
                      <i className="fas fa-users me-2"></i>
                      <span data-key="t-sales-module">Sales Module</span>
                    </a>
                    <ul
                      className={`sub-menu mm-collapse ${openSubmenu === 'sales' ? 'mm-show' : ''}`}
                      aria-expanded={openSubmenu === 'sales'}
                      style={{ display: openSubmenu === 'sales' ? 'block' : 'none' }}
                    >

                      <li className={location.pathname.startsWith('/admin/sales/leads') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/leads" className={location.pathname.startsWith('/admin/sales/leads') ? 'active' : ''}>
                          <i className="fas fa-user-tie me-2"></i>
                          <span data-key="t-sales-leads">Sales Leads</span>
                        </Link>
                      </li>

                      <li className={location.pathname.startsWith('/admin/sales/opportunities') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/opportunities" className={location.pathname.startsWith('/admin/sales/opportunities') ? 'active' : ''}>
                          <i className="fas fa-handshake me-2"></i>
                          <span data-key="t-sales-opportunities">Sales Opportunities</span>
                        </Link>
                      </li>

                      <li className={location.pathname.startsWith('/admin/sales/job-listings') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/job-listings" className={location.pathname.startsWith('/admin/sales/job-listings') ? 'active' : ''}>
                          <i className="fas fa-briefcase me-2"></i>
                          <span data-key="t-job-listings">Job Listings</span>
                        </Link>
                      </li>

                      <li className={location.pathname === '/roi-calculator' ? 'mm-active' : ''}>
                        <Link to="/roi-calculator" className={location.pathname === '/roi-calculator' ? 'active' : ''}>
                          <i className="fas fa-calculator me-2"></i>
                          <span data-key="t-roi-calculator">ROI Calculator</span>
                        </Link>
                      </li>
                      {/* Analytics Dashboard - Only for admins */}
                      {isAnkrAdmin && (
                        <li className={location.pathname === '/admin/sales/analytics' ? 'mm-active' : ''}>
                          <Link to="/admin/sales/analytics" className={location.pathname === '/admin/sales/analytics' ? 'active' : ''}>
                            <i className="fas fa-chart-bar me-2"></i>
                            <span data-key="t-analytics-dashboard">Analytics Dashboard</span>
                          </Link>
                        </li>
                      )}
                      <li className={location.pathname === '/admin/sales/call-logs' ? 'mm-active' : ''}>
                        <Link to="/admin/sales/call-logs" className={location.pathname === '/admin/sales/call-logs' ? 'active' : ''}>
                          <i className="fas fa-person-chalkboard me-2"></i>
                          <span data-key="t-call-logs">Voice AI Coach</span>
                        </Link>
                      </li>

                      <li className="mt-2" data-key="t-admin"><Link className="text-primary" disabled>Sales Admin Tools</Link></li>

                      <li className={location.pathname.startsWith('/admin/sales/organizations') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/organizations" className={location.pathname.startsWith('/admin/sales/organizations') ? 'active' : ''}>
                          <i className="fas fa-building me-2"></i>
                          <span data-key="t-organizations">Organizations</span>
                        </Link>
                      </li>
                      <li className={location.pathname.startsWith('/admin/sales/teams') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/teams" className={location.pathname.startsWith('/admin/sales/teams') ? 'active' : ''}>
                          <i className="fas fa-users me-2"></i>
                          <span data-key="t-sales-teams">Sales Teams</span>
                        </Link>
                      </li>
                      <li className={location.pathname.startsWith('/admin/sales/users') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/users" className={location.pathname.startsWith('/admin/sales/users') ? 'active' : ''}>
                          <i className="fas fa-user-tag me-2"></i>
                          <span data-key="t-sales-users">Sales Users</span>
                        </Link>
                      </li>
                      <li className={location.pathname.startsWith('/admin/sales/roles') ? 'mm-active' : ''}>
                        <Link to="/admin/sales/roles" className={location.pathname.startsWith('/admin/sales/roles') ? 'active' : ''}>
                          <i className="fas fa-id-badge me-2"></i>
                          <span data-key="t-sales-roles">Roles & Permissions</span>
                        </Link>
                      </li>
                      <li className={location.pathname === '/admin/voiceai/credentials' ? 'mm-active' : ''}>
                        <Link to="/admin/voiceai/credentials" className={location.pathname === '/admin/voiceai/credentials' ? 'active' : ''}>
                          <i className="fas fa-key me-2"></i>
                          <span data-key="t-credentials-manager">Credentials Manager</span>
                        </Link>
                      </li>
                    </ul>
                  </li>
                  </>
              )}

              <li className={openSubmenu === 'patient' ? 'mm-active' : ''}>
                <a href="javascript: void(0);" className="has-arrow" onClick={() => toggleSubmenu('patient')} aria-expanded={openSubmenu === 'patient'} >
                  <i className="fas fa-user-injured me-2"></i>
                  <span data-key="t-authentication">Patient Module</span>
                </a>
                <ul
                  className={`sub-menu mm-collapse ${openSubmenu === 'patient' ? 'mm-show' : ''}`}
                  aria-expanded={openSubmenu === 'patient'}
                  style={{ display: openSubmenu === 'patient' ? 'block' : 'none' }}
                >
                <li className={location.pathname === '/check-in' ? 'mm-active' : ''}>
                  <Link to="/check-in" className={location.pathname === '/check-in' ? 'active' : ''}>
                    <i className="fas fa-edit me-2"></i>
                    <span data-key="t-standard-checkin">Standard Check-In</span>
                  </Link>
                </li>
                <li className={location.pathname === '/support-desk' ? 'mm-active' : ''}>
                  <Link to="/support-desk" className={location.pathname === '/support-desk' ? 'active' : ''}>
                    <i className="fas fa-life-ring me-2"></i>
                    <span data-key="t-voice-demo">Support Desk</span>
                  </Link>
                </li>
                </ul>
              </li>
              
              <li className={openSubmenu === 'users' ? 'mm-active' : ''}>
                <a href="javascript: void(0);" className="has-arrow" onClick={() => toggleSubmenu('users')} aria-expanded={openSubmenu === 'users'} >
                  <i className="fas fa-users-cog me-2"></i>
                  <span data-key="t-user-management">User Management</span>
                </a>
                <ul
                  className={`sub-menu mm-collapse ${openSubmenu === 'users' ? 'mm-show' : ''}`}
                  aria-expanded={openSubmenu === 'users'}
                  style={{ display: openSubmenu === 'users' ? 'block' : 'none' }}
                >
                  {isAnkrAdmin && (
                  <>
                  <li className={location.pathname === '/admin/users/ankr' ? 'mm-active' : ''}>
                    <Link to="/admin/users/ankr" className={location.pathname === '/admin/users/ankr' ? 'active' : ''}>
                      <i className="fas fa-user-shield me-2"></i>
                      <span data-key="t-ankr-users">Ankr Users</span>
                    </Link>
                  </li>

                  <li className={location.pathname === '/admin/users' ? 'mm-active' : ''}>
                    <Link to="/admin/users" className={location.pathname === '/admin/users' ? 'active' : ''}>
                      <i className="fas fa-hospital-user me-2"></i>
                      <span data-key="t-all-users">All Users</span>
                    </Link>
                  </li>
                  </>)}

                  <li className={location.pathname === '/admin/users/healthcare' ? 'mm-active' : ''}>
                    <Link to="/admin/users/healthcare" className={location.pathname === '/admin/users/healthcare' ? 'active' : ''}>
                      <i className="fas fa-users me-2"></i>
                      <span data-key="t-healthcare-users">Healthcare Users</span>
                    </Link>
                  </li>

                  <li className={location.pathname.startsWith('/admin/providers') ? 'mm-active' : ''}>
                    <Link to="/admin/providers" className={location.pathname.startsWith('/admin/providers') ? 'active' : ''}>
                      <i className="fas fa-user-md me-1"></i> {/* Icon for providers */}
                      <span data-key="t-providers">Providers</span>
                    </Link>
                  </li>

                  <li className={location.pathname === '/admin/users/patients' ? 'mm-active' : ''}>
                    <Link to="/admin/users/patients" className={location.pathname === '/admin/users/patients' ? 'active' : ''}>
                      <i className="fas fa-user-injured me-2"></i>
                      <span data-key="t-patients-mgmt">Patients</span>
                    </Link>
                  </li>

                  <li className={location.pathname.startsWith('/admin/roles') ? 'mm-active' : ''}>
                    <Link to="/admin/roles" className={location.pathname.startsWith('/admin/roles') ? 'active' : ''}>
                      <i className="fas fa-user-tag me-2"></i>
                      <span data-key="t-roles">Roles & Permissions</span>
                    </Link>
                  </li>
                </ul>
              </li>
              
              {/* Link for Logged In User's Profile */}
              <li className={location.pathname.startsWith('/admin/profile') ? 'mm-active' : ''}>
                <Link to="/admin/profile" className={location.pathname.startsWith('/admin/profile') ? 'active' : ''}>
                  <i className="fas fa-user-circle me-2"></i>
                  <span data-key="t-note-templates">My Profile</span>
                </Link>
              </li>
              
              <li className={location.pathname.startsWith('/admin/forms') ? 'mm-active' : ''}>
                <Link to="/admin/forms" className={location.pathname.startsWith('/admin/forms') ? 'active' : ''}>
                  <i className="fas fa-list-check me-2"></i>
                  <span data-key="t-forms-management">Forms</span>
                </Link>
              </li>

              {/* Link for Note Templates Management */}
              <li className={location.pathname.startsWith('/admin/note-templates') ? 'mm-active' : ''}>
                <Link to="/admin/note-templates" className={location.pathname.startsWith('/admin/note-templates') ? 'active' : ''}>
                  <i className="fas fa-file-alt me-2"></i>
                  <span data-key="t-note-templates">Note Templates</span>
                </Link>
              </li>

              {!isHealthcareUser && (
              <>
              {/* Link for Note Generation Sandbox */}
              <li className={location.pathname.startsWith('/admin/note-generation-sandbox') ? 'mm-active' : ''}>
                <Link to="/admin/note-generation-sandbox" className={location.pathname.startsWith('/admin/note-generation-sandbox') ? 'active' : ''}>
                  <i className="fas fa-vial me-2"></i>
                  <span data-key="t-note-sandbox">Note Sandbox</span>
                </Link>
              </li>

              {/* Link for MFA Module Management */}
              <li className={location.pathname.startsWith('/admin/mfa') ? 'mm-active' : ''}>
                <Link to="/admin/mfa-module" className={location.pathname.startsWith('/admin/mfa-module') ? 'active' : ''}>
                  <i className="fas fa-shield-alt me-2"></i>
                  <span data-key="t-mfa-module">MFA Module</span>
                </Link>
              </li></>
              )}

              {/* Link for Core Server Credentials Management */}
              <li className={location.pathname.startsWith('/admin/credentials') ? 'mm-active' : ''}>
                <Link to="/admin/credentials" className={location.pathname.startsWith('/admin/credentials') ? 'active' : ''}>
                  <i className="fas fa-key me-2"></i>
                  <span data-key="t-credentials">Credentials</span>
                </Link>
              </li>

              {/* Link for Callback Management */}
              {!isHealthcareUser && (
              <>
              <li className={location.pathname.startsWith('/admin/callbacks') ? 'mm-active' : ''}>
                <Link to="/admin/callbacks" className={location.pathname.startsWith('/admin/callbacks') ? 'active' : ''}>
                  <i className="fas fa-exchange-alt me-2"></i> {/* Using exchange-alt icon for callbacks */}
                  <span data-key="t-callbacks">Callbacks</span>
                </Link>
              </li>

              {/* Link for Novu Subscriber Management */}
              <li className={location.pathname.startsWith('/admin/novu-subscribers') ? 'mm-active' : ''}>
                <Link to="/admin/novu-subscribers" className={location.pathname.startsWith('/admin/novu-subscribers') ? 'active' : ''}>
                  <i className="fas fa-users me-2"></i>
                  <span data-key="t-novu-subscribers">Novu Module</span>
                </Link>
              </li>
              </>)}

              {/* Link for Scope Management - Ankr Admin Only */}
              {isAnkrAdmin && (
              <li className={location.pathname.startsWith('/admin/scopes') ? 'mm-active' : ''}>
                <Link to="/admin/scopes" className={location.pathname.startsWith('/admin/scopes') ? 'active' : ''}>
                  <i className="fas fa-shield-halved me-2"></i>
                  <span data-key="t-scopes">Scope Management</span>
                </Link>
              </li>
              )}

              <li>
                <a href="#" onClick={(e) => { e.preventDefault(); logout('/admin/login'); }}>
                  <i className="fas fa-sign-out-alt me-2"></i>
                  <span data-key="t-logout">Logout</span>
                </a>
              </li>
            </>
          )}
        </ul>
        <div className="d-flex d-flex-inline justify-content-between bottom-left">
          <span className="menu-title" style={{paddingRight:'0px !important'}} data-key="t-menu">Version ID: {version}</span>
          <button className="btn btn-sm text-secondary me-2" onClick={(e) => { e.preventDefault(); handleRefreshVersion(); }} title="Refresh version">
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default Navigation;